import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { getRequest } from "../../utils/api"; // Adjust the import path to your API utility
import Loader from "../../Component/Loader/Loader"; // Adjust the import path to your Loader
import PaginationComponent from "../../Component/Pagination/pagination";
import { logout } from "../../utils/auth";
import { toast } from "react-toastify";

// --- 1. Define TypeScript Interfaces ---

interface Plan {
  _id: string;
  plan_title: string;
  plan_selling_price?: number;
  plan_year_discount?: number;
  plan_trial_days?: number;
  plan_show_on_web: number; // Assuming 1 is active, 0 is inactive
  total_subscriptions: number;
  createdAt: string;
}

interface PaginationData {
  total: number;
  limit: number;
  page: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

interface ApiResponse {
  status: string;
  data: Plan[];
  pagination: PaginationData;
  message?: string;
}

// --- 2. Apply Types to Component ---

const Plans: React.FC = () => {
  // Use interfaces to type the state
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);
  const [pagination, setPagination] = useState<PaginationData>({
    total: 0,
    limit: 10,
    page: 1,
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false
  });

  // Fetch plans from API
  const fetchPlansList = async () => {
    setLoading(true);
    try {
      // Assuming getRequestWithToken returns an object matching ApiResponse
      const response = await getRequest(`/plan/plans-pricing?page=${page}`) as ApiResponse;

      if (response.status === "success") {
        setPlans(response.data);
        setPagination(response.pagination);
      } else if (response.status == 'session_expired') {
        setLoading(false)
        logout()
      }
      else {
        setLoading(false)
        toast.error(response.message);
      }
    } catch (error) {
      console.error("Error fetching plans:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlansList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Handle Pagination logic
  const handleNextPage = () => {
    if (pagination.hasNextPage) setPage(prev => prev + 1);
  };

  const handlePrevPage = () => {
    if (pagination.hasPrevPage) setPage(prev => prev - 1);
  };

  return (
    <>
      {loading && <Loader />}
      <div className="hk-pg-wrapper">
        <div className="hk-pg-body py-0">
          <div className="page-wrap">
            <div className="page-content ps-0">
              <div className="page-detail-wrap">
                <header className="page-header">
                  <div>
                    <div className="page-title">
                      <div>
                        <h2 className="mb-1">Manage Plans & Pricing</h2>
                        <p className="mb-0 fs-13">Control subscriptions, pricing, and billing settings in one place</p>
                      </div>
                    </div>
                  </div>
                  <div className="page-options-wrap">
                    <div className="v-separator d-lg-inline-block d-none"></div>
                    <Link to="/add-plan" className="btn btn-dark btn-rounded">
                      <span>
                        <span className="icon"><i className="ri-add-line bticon"></i></span>
                        <span>Create New</span>
                      </span>
                    </Link>

                    <a className="btn btn-icon btn-flush-dark btn-rounded flush-soft-hover hk-navbar-togglable d-lg-inline-block d-none"
                      href="#" data-bs-toggle="tooltip" data-bs-placement="top" title=""
                      data-bs-original-title="Collapse">
                      <span className="btn-icon-wrap">
                        <span className="feather-icon"><i className="ri-arrow-up-s-line ri-lg"></i></span>
                        <span className="feather-icon d-none"><i className="ri-arrow-down-s-line ri-lg"></i></span>
                      </span>
                    </a>
                  </div>
                </header>
                <div className="page-body">
                  <div data-simplebar className="nicescroll-bar">
                    <div className="container-fluid">
                      <div className="row">
                        <div className="col-lg-12">
                          <div className="card">
                            <div className="table-responsive">
                              <table className="table table-hover mb-0">
                                <thead>
                                  <tr>
                                    <th>Plan Name</th>
                                    <th>Monthly Price</th>
                                    <th>Yearly Price</th>
                                    <th className="text-center">Trial Days</th>
                                    <th className="text-center">Total Subscription</th>
                                    <th className="text-center">Status</th>
                                    <th>Create Date</th>
                                    <th className="text-center">Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {plans.length > 0 ? (
                                    plans.map((plan) => (
                                      <tr key={plan._id}>
                                        <td><span className="fw-bold">{plan.plan_title}</span></td>
                                        <td><span className="fw-bold">₹{plan.plan_selling_price || 0}</span></td>
                                        <td><span className="fw-bold">₹{(plan.plan_selling_price || 0) - (plan.plan_year_discount || 0)}</span></td>
                                        <td className="text-center">{plan?.plan_trial_days || 0}</td>
                                        <td className="text-center">{plan?.total_subscriptions}</td>
                                        <td className="text-center">
                                          {plan.plan_show_on_web === 1 ? (
                                            <span className="badge bg-success">Active</span>
                                          ) : (
                                            <span className="badge bg-secondary">Inactive</span>
                                          )}
                                        </td>
                                        <td>{moment(plan.createdAt).format('DD MMM YYYY')}</td>
                                        <td className="text-center">
                                          <div className="d-flex align-items-center justify-content-center gap-1">
                                            <div className="d-flex gap-1">
                                              <Link
                                                to={`/add-plan/${plan._id}`}
                                                className="btn btn-icon btn-sm btn-flush-dark btn-border flush-soft-hover"
                                                data-bs-toggle="tooltip"
                                                data-placement="top"
                                                title="Edit"
                                              >
                                                <span className="icon">
                                                  <i className="ri-pencil-fill"></i>
                                                </span>
                                              </Link>
                                            </div>
                                          </div>
                                        </td>
                                      </tr>
                                    ))
                                  ) : (
                                    !loading && (
                                      <tr>
                                        <td colSpan={8} className="text-center py-4">No plans found.</td>
                                      </tr>
                                    )
                                  )}
                                </tbody>
                              </table>
                            </div>

                            <div className="card-footer d-flex justify-content-between align-items-center">
                              <div className="col-sm">
                                <div className="text-muted">
                                  Showing <span className="fw-semibold">{plans?.length || 0}</span> of <span className="fw-semibold">{pagination.total || 0}</span> Results
                                </div>
                              </div>
                              {pagination.totalPages > 0 && (
                                <PaginationComponent
                                  totalPages={pagination.totalPages}
                                  currentPage={page}
                                  setCurrentPage={setPage}
                                />
                              )}
                            </div>

                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Plans;